<template>
  <div class="order-view">
    <div class="ov-head">
      <!-- 业态 Tab：全部/堂食/快餐/外卖（Task 10.2 外卖 Tab 激活） -->
      <a-tabs v-model="activeTab" size="small" class="ov-tabs" @change="onTabChange">
        <a-tab-pane tab="全部" key="all" />
        <a-tab-pane tab="堂食" key="dine_in" />
        <a-tab-pane tab="快餐" key="fastfood" />
        <a-tab-pane tab="外卖" key="takeout" />
      </a-tabs>
      <div class="ov-filters">
        <a-select v-model="queryStatus" style="width: 140px" @change="reload">
          <a-select-option value="">全部状态</a-select-option>
          <a-select-option v-for="s in currentStatusOptions" :key="s.value" :value="s.value">{{ s.label }}</a-select-option>
        </a-select>
        <a-range-picker @change="onDateChange" />
        <a-button type="primary" icon="search" :loading="loading" @click="reload">查询</a-button>
      </div>
    </div>
    <div class="ov-body">
      <a-table :columns="currentColumns" :data-source="rows" :loading="loading" rowKey="id"
               size="middle" :pagination="pagination" :scroll="{ x: 1080 }" @change="handleTableChange">
        <template slot="tableNo" slot-scope="text, record">
          <span v-if="record.call_no" class="ov-callno">取餐号 {{ record.call_no }}</span>
          <span v-else>{{ record.tableName || '-' }}</span>
        </template>
        <template slot="amount" slot-scope="text">
          <span class="ov-amount">¥{{ fmt(text) }}</span>
        </template>
        <template slot="status" slot-scope="text, record">
          <a-tag v-if="record.state !== undefined" :color="takeoutStatusColor(record.state)">{{ record.stateText || takeoutStatusText(record.state) }}</a-tag>
          <a-tag v-else :color="statusColor(record.status)">{{ record.statusText || statusText(record.status) }}</a-tag>
        </template>
        <template slot="action" slot-scope="text, record">
          <!-- 外卖行：详情/接单/出餐/完成/自提核销/打印（Task 10.2） -->
          <template v-if="record.state !== undefined">
            <a-button size="small" @click="showTakeoutDetail(record)">详情</a-button>
            <a-button v-if="canTakeoutReceive(record)" size="small" type="primary" class="act-btn"
                      :loading="takeoutOpId === record.id" @click="handleTakeoutOp(record, 'receive')">接单</a-button>
            <a-button v-if="canTakeoutMealOut(record)" size="small" class="act-btn"
                      :loading="takeoutOpId === record.id" @click="handleTakeoutOp(record, 'mealOut')">出餐</a-button>
            <a-button v-if="canTakeoutComplete(record)" size="small" class="act-btn"
                      :loading="takeoutOpId === record.id" @click="handleTakeoutOp(record, 'complete')">完成</a-button>
            <a-button v-if="canTakeoutVerify(record)" size="small" type="primary" class="act-btn"
                      @click="openVerify(record)">自提核销</a-button>
            <a-button size="small" class="act-btn" @click="showTakeoutPrint(record)">打印</a-button>
          </template>
          <!-- 堂食/快餐行 -->
          <template v-else>
            <a-button size="small" @click="showDetail(record)">详情</a-button>
            <a-button v-if="canReceive(record)" size="small" type="primary" class="act-btn"
                      :loading="receivingId === record.id" @click="handleReceive(record)">接单</a-button>
            <a-popconfirm v-if="record.status === '3' && canRefund" title="确认整单退款？"
                          @confirm="handleRefund(record)">
              <a-button size="small" class="act-btn" type="danger">退款</a-button>
            </a-popconfirm>
            <a-button size="small" class="act-btn" @click="showPrint(record)">打印</a-button>
          </template>
        </template>
      </a-table>
    </div>

    <!-- 订单详情弹窗：头信息 + 菜品选项快照 + 金额明细（服务费/打包费行） -->
    <a-modal :visible="detailVisible" title="订单详情" :footer="null" width="760px"
             @cancel="detailVisible = false">
      <template v-if="detailData">
        <div class="detail-grid">
          <div class="d-item"><span class="d-k">订单号</span>{{ detailData.order_no }}</div>
          <div class="d-item"><span class="d-k">状态</span>{{ detailData.statusText || statusText(detailData.status) }}</div>
          <div class="d-item"><span class="d-k">业态</span>{{ businessText(detailData) }}</div>
          <div class="d-item"><span class="d-k">桌台/取餐号</span>{{ detailData.call_no ? '取餐号 ' + detailData.call_no : (detailData.tableName || '-') }}</div>
          <div class="d-item"><span class="d-k">会员</span>{{ detailData.memberName || '-' }}</div>
          <div class="d-item"><span class="d-k">人数</span>{{ detailData.person_count || detailData.people || '-' }}</div>
          <div class="d-item"><span class="d-k">消费方式</span>{{ detailData.is_out === '2' ? '打包带走' : '店内就餐' }}</div>
          <div class="d-item"><span class="d-k">收银员</span>{{ detailData.operatorName || detailData.creatorName || '-' }}</div>
          <div class="d-item"><span class="d-k">下单时间</span>{{ detailData.create_time || '-' }}</div>
          <div class="d-item"><span class="d-k">结算时间</span>{{ detailData.settle_time || '-' }}</div>
        </div>
        <a-table :columns="itemColumns" :data-source="detailData.items || []"
                 rowKey="id" size="small" :pagination="false" :scroll="{ y: 240 }" />
        <div class="detail-amounts">
          <div class="da-row"><span>商品合计</span><span>¥{{ fmt(detailData.total_amount) }}</span></div>
          <div class="da-row" v-if="Number(detailData.service_money) > 0"><span>服务费</span><span>¥{{ fmt(detailData.service_money) }}</span></div>
          <div class="da-row" v-if="Number(detailData.tableware_money) > 0"><span>打包费</span><span>¥{{ fmt(detailData.tableware_money) }}</span></div>
          <div class="da-row" v-if="Number(detailData.discount_amount) > 0"><span>会员优惠</span><span>-¥{{ fmt(detailData.discount_amount) }}</span></div>
          <div class="da-row" v-if="Number(detailData.erase_amount) > 0"><span>抹零</span><span>-¥{{ fmt(detailData.erase_amount) }}</span></div>
          <div class="da-row da-pay"><span>实收</span><span>¥{{ fmt(detailData.pay_amount) }}</span></div>
          <div class="da-row"><span>支付方式</span><span>{{ payText(detailData) }}</span></div>
        </div>
      </template>
    </a-modal>

    <!-- 外卖订单详情弹窗：头信息 + 地址快照 + 明细 + 时间线 + 费用明细（Task 10.2） -->
    <a-modal :visible="takeoutDetailVisible" title="外卖订单详情" :footer="null" width="760px"
             @cancel="takeoutDetailVisible = false">
      <template v-if="takeoutDetailData">
        <div class="detail-grid">
          <div class="d-item"><span class="d-k">订单号</span>{{ takeoutDetailData.orderNo }}</div>
          <div class="d-item"><span class="d-k">状态</span>{{ takeoutDetailData.stateText }}</div>
          <div class="d-item"><span class="d-k">方式</span>{{ takeoutDetailData.deliveryModeText }}</div>
          <div class="d-item"><span class="d-k">取单号</span>{{ takeoutDetailData.takeNo || '-' }}</div>
          <div class="d-item"><span class="d-k">自提码</span>{{ takeoutDetailData.selfCode || '-' }}</div>
          <div class="d-item"><span class="d-k">预约时间</span>{{ takeoutDetailData.reserveTime || '-' }}</div>
          <div class="d-item"><span class="d-k">出餐状态</span>{{ takeoutDetailData.mealOutState === '1' ? '已出餐' : '未出餐' }}</div>
          <div class="d-item"><span class="d-k">下单时间</span>{{ takeoutDetailData.createTime || '-' }}</div>
          <div class="d-item"><span class="d-k">支付时间</span>{{ takeoutDetailData.settleTime || '-' }}</div>
          <div class="d-item"><span class="d-k">接单时间</span>{{ takeoutDetailData.receiptAt || '-' }}</div>
          <div class="d-item"><span class="d-k">完成时间</span>{{ takeoutDetailData.completeAt || '-' }}</div>
        </div>
        <div class="to-addr" v-if="takeoutDetailData.address">
          <div class="to-addr-title">收货地址</div>
          <div class="to-addr-line">{{ takeoutAddrContact }} {{ takeoutDetailData.address.phone }}</div>
          <div class="to-addr-line">{{ takeoutAddrText }}</div>
        </div>
        <div class="to-remark" v-if="takeoutDetailData.remark">备注：{{ takeoutDetailData.remark }}</div>
        <a-table :columns="takeoutItemColumns" :data-source="takeoutDetailData.items || []"
                 rowKey="materialName" size="small" :pagination="false" :scroll="{ y: 200 }" />
        <div class="detail-amounts">
          <div class="da-row"><span>商品合计</span><span>¥{{ fmt(takeoutDetailData.goodsAmount) }}</span></div>
          <div class="da-row"><span>配送费</span><span>¥{{ fmt(takeoutDetailData.deliveryAmount) }}</span></div>
          <div class="da-row"><span>包装费</span><span>¥{{ fmt(takeoutDetailData.boxAmount) }}</span></div>
          <div class="da-row da-pay"><span>实收</span><span>¥{{ fmt(takeoutDetailData.payAmount) }}</span></div>
        </div>
        <!-- 状态时间线（下单→支付→接单→完成，取消单走取消节点） -->
        <div class="to-timeline">
          <div class="to-timeline-title">订单进度</div>
          <div class="to-step" :class="{ done: s.done }" v-for="(s, i) in takeoutTimeline" :key="i">
            <span class="to-step-dot"></span>
            <span class="to-step-text">{{ s.text }}</span>
            <span class="to-step-time">{{ s.time || '' }}</span>
          </div>
        </div>
      </template>
    </a-modal>

    <!-- 自提码核销弹窗（Task 10.2：输入顾客自提码 → verify-self） -->
    <a-modal :visible="verifyVisible" title="自提核销" okText="核销" cancelText="取消"
             :confirmLoading="takeoutOpId === verifyTargetId"
             @ok="handleVerify" @cancel="verifyVisible = false">
      <div class="verify-tip" v-if="verifyTarget">订单 {{ verifyTarget.orderNo }}（{{ verifyTarget.stateText }}）</div>
      <a-input v-model="verifyCode" placeholder="请输入顾客出示的自提码" maxlength="12"
               @pressEnter="handleVerify" />
    </a-modal>

    <!-- 打印数据弹窗（一期 print-data 展示模式） -->
    <a-modal :visible="printVisible" title="厨打 / 结账单数据" :footer="null" width="560px"
             @cancel="printVisible = false">
      <pre class="print-pre">{{ printText }}</pre>
    </a-modal>
  </div>
</template>

<script>
import { getAction, postAction } from '@/api/manage'
import { orderList, orderDetail, orderPrintData, orderRefund,
  takeoutList, takeoutDetail, takeoutReceive, takeoutMealOut, takeoutComplete,
  takeoutVerifySelf, takeoutPrintData } from '@/api/plugin/dining'
import { getUserBtnByCurrentUser } from '@/api/api'

const PREFIX = '/api/plugin/dining'
const ORDER_URL = '/plugin/dining/order' // 餐饮订单功能 2904 的菜单 url（退款权限位）

/**
 * 订单视图（/cashier/dining/order，Task 7.4 克隆云贝 + Task 10.2 外卖 Tab）：
 * 业态 Tab（全部/堂食/快餐/外卖）+ 云贝数字状态筛选 + 列表
 * （单号/桌台或取餐号/会员/金额/支付方式/状态 statusText/收银员/时间）
 * + 操作：详情（菜品+选项快照+金额明细含服务费/打包费行）、接单（待接单，
 * 契约接口 POST /order/{id}/receive）、退款（一期 refund 确认弹窗+权限位）、
 * 打印（一期 print-data 弹窗展示）。
 * 外卖 Tab（Task 10.2）：GET /takeout-orders/list（state 筛选）+ 操作
 * 接单 '2'→'3' / 出餐 meal-out / 完成 '3'|'4'→'5' / 自提核销弹窗输入自提码
 * verify-self；详情弹窗（地址快照+费用明细+状态时间线）；打印外卖版 print-data。
 */
export default {
  name: 'CashierOrderView',
  props: {
    storeId: { type: Number, required: true },
    storeName: { type: String, default: '' }
  },
  data() {
    return {
      activeTab: 'all',
      queryStatus: '',
      dateBegin: '',
      dateEnd: '',
      rows: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      loading: false,
      instore: {},
      fastSet: {},
      canRefund: false,
      receivingId: null,
      detailVisible: false,
      detailData: null,
      printVisible: false,
      printText: '',
      // 外卖 Tab（Task 10.2）
      takeoutOpId: null,          // 外卖操作中行 id（接单/出餐/完成/核销）
      takeoutDetailVisible: false,
      takeoutDetailData: null,
      verifyVisible: false,
      verifyCode: '',
      verifyTarget: null,
      statusOptions: [
        { value: '1', label: '待付款/待接单' },
        { value: '2', label: '就餐中/已付款' },
        { value: '3', label: '已完成' },
        { value: 'parked', label: '挂单中' },
        { value: '5', label: '已取消' },
        { value: '6', label: '申请退款' },
        { value: '7', label: '退款通过' },
        { value: '8', label: '退款拒绝' },
        { value: '9', label: '已拒单' }
      ],
      // 外卖状态筛选（TakeoutStatus：1未付款 2已付款待接单 3已接单 4配送中 5已完成 7已取消）
      takeoutStatusOptions: [
        { value: '2', label: '待接单' },
        { value: '3', label: '已接单' },
        { value: '4', label: '配送中' },
        { value: '5', label: '已完成' }
      ],
      columns: [
        { title: '单号', dataIndex: 'order_no', width: 170 },
        { title: '桌台/取餐号', key: 'tableNo', width: 120, scopedSlots: { customRender: 'tableNo' } },
        { title: '会员', dataIndex: 'memberName', width: 90, customRender: t => t || '-' },
        { title: '合计', dataIndex: 'total_amount', width: 90, align: 'right', scopedSlots: { customRender: 'amount' } },
        { title: '支付方式', key: 'pay', width: 90, customRender: (t, r) => this.payText(r) },
        { title: '状态', dataIndex: 'status', width: 96, scopedSlots: { customRender: 'status' } },
        { title: '收银员', dataIndex: 'creatorName', width: 90, customRender: t => t || '-' },
        { title: '时间', dataIndex: 'create_time', width: 155 },
        { title: '操作', key: 'action', width: 200, fixed: 'right', scopedSlots: { customRender: 'action' } }
      ],
      // 外卖列表列（驼峰字段，独立表 jsh_dining_takeout_order）
      takeoutColumns: [
        { title: '单号', dataIndex: 'orderNo', width: 170 },
        { title: '方式', key: 'mode', width: 96, customRender: (t, r) => (String(r.deliveryMode) === '10' ? '到店自提' : '外卖配送') },
        { title: '取单号/自提码', key: 'takeNo', width: 130, customRender: (t, r) => r.selfCode ? ('自提码 ' + r.selfCode) : (r.takeNo ? '取单 ' + r.takeNo : '-') },
        { title: '预约时间', dataIndex: 'reserveTime', width: 140, customRender: t => t || '-' },
        { title: '合计', dataIndex: 'payAmount', width: 90, align: 'right', scopedSlots: { customRender: 'amount' } },
        { title: '状态', key: 'state', width: 96, scopedSlots: { customRender: 'status' } },
        { title: '时间', dataIndex: 'createTime', width: 155 },
        { title: '操作', key: 'action', width: 300, fixed: 'right', scopedSlots: { customRender: 'action' } }
      ],
      itemColumns: [
        { title: '菜品', dataIndex: 'material_name', width: 130 },
        { title: '选项', key: 'options', width: 150, customRender: (t, r) => this.optionsText(r.options_json) },
        { title: '单价', dataIndex: 'unit_price', width: 80, align: 'right', customRender: v => '¥' + this.fmt(v) },
        { title: '数量', dataIndex: 'quantity', width: 70, align: 'right' },
        { title: '小计', dataIndex: 'all_price', width: 88, align: 'right', customRender: v => '¥' + this.fmt(v) },
        { title: '标记', dataIndex: 'item_status', width: 70, customRender: t => t === 'added' ? '加菜' : '首单' },
        { title: '备注', dataIndex: 'remark', width: 90, customRender: t => t || '-' }
      ],
      // 外卖明细列（选项已为快照文本）
      takeoutItemColumns: [
        { title: '菜品', dataIndex: 'materialName', width: 150 },
        { title: '选项', dataIndex: 'options', width: 150, customRender: t => t || '-' },
        { title: '单价', dataIndex: 'unitPrice', width: 80, align: 'right', customRender: v => '¥' + this.fmt(v) },
        { title: '数量', dataIndex: 'quantity', width: 70, align: 'right' },
        { title: '小计', dataIndex: 'allPrice', width: 88, align: 'right', customRender: v => '¥' + this.fmt(v) },
        { title: '备注', dataIndex: 'remark', width: 90, customRender: t => t || '-' }
      ]
    }
  },
  computed: {
    pagination() {
      return {
        current: this.currentPage,
        pageSize: this.pageSize,
        total: this.total,
        showSizeChanger: false,
        showTotal: t => `共 ${t} 条`
      }
    },
    isTakeout() {
      return this.activeTab === 'takeout'
    },
    currentColumns() {
      return this.isTakeout ? this.takeoutColumns : this.columns
    },
    currentStatusOptions() {
      return this.isTakeout ? this.takeoutStatusOptions : this.statusOptions
    },
    takeoutAddrContact() {
      const addr = this.takeoutDetailData && this.takeoutDetailData.address
      return (addr && addr.contact) || '-'
    },
    takeoutAddrText() {
      const addr = this.takeoutDetailData && this.takeoutDetailData.address
      if (!addr) return ''
      return [addr.province, addr.city, addr.district, addr.detail].filter(Boolean).join('')
    },
    // 外卖时间线（后端 timeline 直渲）
    takeoutTimeline() {
      return (this.takeoutDetailData && this.takeoutDetailData.timeline) || []
    },
    verifyTargetId() {
      return (this.verifyTarget && this.verifyTarget.id) || null
    }
  },
  created() {
    this.loadRefundPermission()
    this.loadReceivingSet()
    this.load()
  },
  methods: {
    fmt(v) {
      return Number(v || 0).toFixed(2)
    },
    statusText(s) {
      return { '1': '待付款', '2': '就餐中/已付款', '3': '已完成', '5': '已取消', '6': '申请退款',
        '7': '退款通过', '8': '退款拒绝', '9': '已拒单', parked: '挂单中' }[s] || s
    },
    statusColor(s) {
      return { '1': 'orange', '2': 'blue', '3': 'green', '5': 'red', '6': 'orange',
        '7': 'red', '8': 'default', '9': 'red', parked: 'purple' }[s] || 'default'
    },
    // 外卖状态文案/配色（TakeoutStatus）
    takeoutStatusText(s) {
      return { '1': '待付款', '2': '已付款', '3': '已接单', '4': '配送中', '5': '已完成',
        '7': '已取消', '9': '申请退款', '10': '已退款' }[s] || s
    },
    takeoutStatusColor(s) {
      return { '1': 'orange', '2': 'orange', '3': 'blue', '4': 'blue', '5': 'green',
        '7': 'default', '9': 'orange', '10': 'default' }[s] || 'default'
    },
    businessText(order) {
      return order.business_type === 'fastfood' ? '快餐' : '堂食'
    },
    // 支付方式文案：v2 pay_type 存中文文本，存量为 cash/wx/ali 码，payment 为数字类别兜底
    payText(order) {
      const pt = order.pay_type
      if (pt) {
        return { cash: '现金', wx: '微信', ali: '支付宝' }[pt] || pt
      }
      const code = String(order.payment || '')
      return { '1': '线上支付', '2': '现金', '3': '余额' }[code] || '-'
    },
    optionsText(json) {
      if (!json) return '-'
      try {
        const arr = JSON.parse(String(json))
        if (!Array.isArray(arr)) return '-'
        return arr.map(o => o.valueName).filter(Boolean).join('/') || '-'
      } catch (e) {
        return '-'
      }
    },
    // 接单可见性：堂食 '1' 待接单 + instore.receiving='2'；快餐 '2' 已付款待接单 + fast.receiving='2'
    canReceive(record) {
      if (record.business_type === 'fastfood') {
        return record.status === '2' && String(this.fastSet.receiving) === '2'
      }
      return record.status === '1' && String(this.instore.receiving) === '2'
    },
    // ===== 外卖操作可见性（Task 10.2） =====
    // 接单：'2' 已付款待接单
    canTakeoutReceive(record) {
      return record.state === '2'
    },
    // 出餐：mealOutState 未标记 + 状态 '2'/'3'
    canTakeoutMealOut(record) {
      return String(record.mealOutState) !== '1' && (record.state === '2' || record.state === '3')
    },
    // 完成：配送单 '3' 已接单 / '4' 配送中（自提单走核销完成）
    canTakeoutComplete(record) {
      return String(record.deliveryMode) !== '10' && (record.state === '3' || record.state === '4')
    },
    // 自提核销：自提单 + '2'/'3' 状态
    canTakeoutVerify(record) {
      return String(record.deliveryMode) === '10' && (record.state === '2' || record.state === '3')
    },
    // 店内设置（receiving：1=自动接单 2=手动接单）
    loadReceivingSet() {
      getAction(`${PREFIX}/store-setting/instore`, { storeId: this.storeId }).then(res => {
        if (res.code === 200 && res.data) this.instore = res.data
      }).catch(() => {})
      getAction(`${PREFIX}/store-setting/fast`, { storeId: this.storeId }).then(res => {
        if (res.code === 200 && res.data) this.fastSet = res.data
      }).catch(() => {})
    },
    /**
     * 退款权限位（一期 OrderManage 模式）：GET /user/getUserBtnByCurrentUser →
     * data.userBtn = [{url, btnStr}]；admin 无 userBtn 视为全权限；找 2904 的 btnStr 含 '8'
     */
    loadRefundPermission() {
      getUserBtnByCurrentUser().then(res => {
        const btnList = res && res.data && res.data.userBtn
        if (!btnList) {
          this.canRefund = true
          return
        }
        this.canRefund = btnList.some(item => item.url === ORDER_URL &&
          item.btnStr && item.btnStr.indexOf('8') > -1)
      }).catch(() => {
        this.canRefund = false
      })
    },
    onDateChange(dates, dateStrings) {
      this.dateBegin = (dateStrings && dateStrings[0]) || ''
      this.dateEnd = (dateStrings && dateStrings[1]) || ''
    },
    reload() {
      this.currentPage = 1
      this.load()
    },
    // 切换业态 Tab：外卖独立状态机（1/2/3/4/5/7），状态枚举不同须重置状态筛选
    onTabChange() {
      this.queryStatus = ''
      this.reload()
    },
    load() {
      if (this.isTakeout) {
        this.loadTakeout()
        return
      }
      this.loading = true
      const params = {
        storeId: this.storeId,
        currentPage: this.currentPage,
        pageSize: this.pageSize
      }
      // 业态 Tab → order_type 过滤（fastfood 单 order_type='takeout'，堂食 'dine_in'）
      if (this.activeTab === 'dine_in') params.orderType = 'dine_in'
      if (this.activeTab === 'fastfood') params.orderType = 'takeout'
      if (this.queryStatus) params.status = this.queryStatus
      if (this.dateBegin) params.dateBegin = this.dateBegin
      if (this.dateEnd) params.dateEnd = this.dateEnd
      orderList(params).then(res => {
        if (res.code === 200 && res.data) {
          this.rows = res.data.rows || []
          this.total = res.data.total || 0
        } else {
          this.$message.error(res.message || '查询失败')
        }
      }).catch(() => {}).finally(() => {
        this.loading = false
      })
    },
    // 外卖订单列表（Task 10.2）：GET /takeout-orders/list?storeId&state
    loadTakeout() {
      this.loading = true
      const params = {
        storeId: this.storeId,
        currentPage: this.currentPage,
        pageSize: this.pageSize
      }
      if (this.queryStatus) params.state = this.queryStatus
      takeoutList(params).then(res => {
        if (res.code === 200 && res.data) {
          this.rows = res.data.rows || []
          this.total = res.data.total || 0
        } else {
          this.$message.error(res.message || '查询失败')
        }
      }).catch(() => {}).finally(() => {
        this.loading = false
      })
    },
    handleTableChange(pag) {
      this.currentPage = pag.current
      this.load()
    },
    showDetail(record) {
      orderDetail(record.id).then(res => {
        if (res.code === 200 && res.data) {
          this.detailData = res.data
          this.detailVisible = true
        } else {
          this.$message.error(res.message || '查询详情失败')
        }
      }).catch(() => {})
    },
    // 手动接单：契约接口 POST /order/{id}/receive（堂食 '1'→'2'+receipt_at / 快餐 '2'→'3'）
    handleReceive(record) {
      if (this.receivingId) return
      this.receivingId = record.id
      postAction(`${PREFIX}/order/${record.id}/receive`).then(res => {
        if (res.code === 200) {
          this.$message.success('接单成功')
          this.load()
        } else {
          this.$message.error(res.message || '接单失败')
        }
      }).catch(() => {
        this.$message.error('接单失败，请重试')
      }).finally(() => {
        this.receivingId = null
      })
    },
    // ===== 外卖操作（Task 10.2）：接单/出餐/完成 =====
    handleTakeoutOp(record, op) {
      if (this.takeoutOpId) return
      const api = { receive: takeoutReceive, mealOut: takeoutMealOut, complete: takeoutComplete }[op]
      const tip = { receive: '接单成功', mealOut: '已标记出餐', complete: '订单已完成' }[op]
      this.takeoutOpId = record.id
      api(record.id).then(res => {
        if (res.code === 200) {
          this.$message.success(tip)
          this.loadTakeout()
        } else {
          this.$message.error(res.message || '操作失败')
        }
      }).catch(() => {
        this.$message.error('操作失败，请重试')
      }).finally(() => {
        this.takeoutOpId = null
      })
    },
    // 外卖详情弹窗：地址快照 + 明细 + 时间线 + 费用明细
    showTakeoutDetail(record) {
      takeoutDetail(record.id).then(res => {
        if (res.code === 200 && res.data) {
          this.takeoutDetailData = res.data
          this.takeoutDetailVisible = true
        } else {
          this.$message.error(res.message || '查询详情失败')
        }
      }).catch(() => {})
    },
    // 自提核销弹窗
    openVerify(record) {
      this.verifyTarget = record
      this.verifyCode = ''
      this.verifyVisible = true
    },
    handleVerify() {
      const target = this.verifyTarget
      if (!target || this.takeoutOpId === target.id) return
      const code = (this.verifyCode || '').trim()
      if (!code) {
        this.$message.warning('请输入顾客出示的自提码')
        return
      }
      this.takeoutOpId = target.id
      takeoutVerifySelf(target.id, { selfCode: code }).then(res => {
        if (res.code === 200) {
          this.$message.success('核销成功，订单已完成')
          this.verifyVisible = false
          this.loadTakeout()
        } else {
          this.$message.error(res.message || '核销失败')
        }
      }).catch(() => {
        this.$message.error('核销失败，请重试')
      }).finally(() => {
        this.takeoutOpId = null
      })
    },
    handleRefund(record) {
      orderRefund(record.id).then(res => {
        if (res.code === 200) {
          this.$message.success('退款成功')
          this.load()
        } else {
          this.$message.error(res.message || '退款失败')
        }
      }).catch(() => {})
    },
    showPrint(record) {
      orderPrintData(record.id).then(res => {
        if (res.code === 200 && res.data) {
          this.printText = JSON.stringify(res.data, null, 2)
          this.printVisible = true
        } else {
          this.$message.error(res.message || '查询打印数据失败')
        }
      }).catch(() => {})
    },
    // 外卖打印数据（外卖版 print-data）
    showTakeoutPrint(record) {
      takeoutPrintData(record.id).then(res => {
        if (res.code === 200 && res.data) {
          this.printText = JSON.stringify(res.data, null, 2)
          this.printVisible = true
        } else {
          this.$message.error(res.message || '查询打印数据失败')
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.order-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #f5f6f8;
}
.ov-head {
  flex-shrink: 0;
  background: #fff;
  padding: 0 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
}
.ov-tabs {
  flex: 1;
  min-width: 320px;
}
.ov-tabs /deep/ .ant-tabs-bar {
  margin-bottom: 0;
  border-bottom: none;
}
.ov-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 0 12px;
}
.ov-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 16px 16px;
}
.ov-callno {
  color: #1890ff;
  font-weight: 600;
}
.ov-amount {
  color: #e4632a;
  font-weight: 600;
}
.act-btn {
  margin-left: 8px;
}
.detail-grid {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.d-item {
  width: 33.3%;
  font-size: 13px;
  color: #262b33;
  padding: 4px 0;
}
.d-k {
  color: #98a0aa;
  margin-right: 8px;
}
.detail-amounts {
  margin-top: 14px;
  border-top: 1px dashed #e4e7ed;
  padding-top: 10px;
}
.da-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #4a5261;
  padding: 4px 4px;
}
.da-pay {
  color: #e4632a;
  font-size: 17px;
  font-weight: 700;
}
.print-pre {
  max-height: 60vh;
  overflow: auto;
  background: #fafbfc;
  border: 1px solid #eef0f3;
  border-radius: 6px;
  padding: 12px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
}
/* 外卖详情：地址快照 + 备注 + 时间线 */
.to-addr {
  background: #f7f9fc;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 12px;
}
.to-addr-title {
  font-size: 13px;
  color: #98a0aa;
  margin-bottom: 4px;
}
.to-addr-line {
  font-size: 13px;
  color: #262b33;
  line-height: 1.6;
}
.to-remark {
  font-size: 13px;
  color: #e4632a;
  margin-bottom: 12px;
}
.to-timeline {
  margin-top: 14px;
  border-top: 1px dashed #e4e7ed;
  padding-top: 10px;
}
.to-timeline-title {
  font-size: 13px;
  color: #98a0aa;
  margin-bottom: 8px;
}
.to-step {
  display: flex;
  align-items: center;
  padding: 3px 0;
}
.to-step-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d9dee5;
  margin-right: 10px;
  flex-shrink: 0;
}
.to-step.done .to-step-dot {
  background: #1890ff;
}
.to-step-text {
  font-size: 13px;
  color: #98a0aa;
  min-width: 72px;
}
.to-step.done .to-step-text {
  color: #262b33;
}
.to-step-time {
  font-size: 12px;
  color: #b6bdc7;
}
.verify-tip {
  font-size: 13px;
  color: #4a5261;
  margin-bottom: 10px;
}
</style>
